import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const PG_CART_RECOMMENDATIONS_TAG = 'pg-cart-recommendations';
const PG_CART_OPTIONS_CLASS = 'pg-cart-options-panel';
const CART_DRAWER_CLOSE_EVENT = 'theme-drawer:close';
const MANAGED_INERT_ATTRIBUTE = 'data-pg-cart-stability-inert';

function mountCartRecommendations(cartDrawerComponent) {
  if (!(cartDrawerComponent instanceof Element)) return;
  const itemsHost = cartDrawerComponent.querySelector('.cart-drawer__items');
  const isEmpty = Boolean(cartDrawerComponent.querySelector('.cart-drawer--empty'));
  const existing = cartDrawerComponent.querySelector(PG_CART_RECOMMENDATIONS_TAG);
  if (!itemsHost || isEmpty) {
    existing?.remove();
    return;
  }
  if (!existing) itemsHost.appendChild(document.createElement(PG_CART_RECOMMENDATIONS_TAG));
}

class StableCartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #closeGeneration = 0;
  #optionsObserver = null;

  get #themeDrawer() { return this.closest('theme-drawer'); }
  get #dialog() { return this.closest('dialog'); }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(CART_DRAWER_CLOSE_EVENT, this.#handleDrawerClose);
    this.addEventListener('click', this.#prepareOptionsPanelClose, { capture: true });
    this.addEventListener('keydown', this.#prepareOptionsPanelClose, { capture: true });

    this.#optionsObserver = new MutationObserver(this.#syncOptionsPanelState);
    this.#optionsObserver.observe(this, { childList: true, subtree: true });
    this.#syncOptionsPanelState();

    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(CART_DRAWER_CLOSE_EVENT, this.#handleDrawerClose);
    this.removeEventListener('click', this.#prepareOptionsPanelClose, { capture: true });
    this.removeEventListener('keydown', this.#prepareOptionsPanelClose, { capture: true });
    this.#optionsObserver?.disconnect();
    this.#optionsObserver = null;
    this.#restoreManagedInert();
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    mountCartRecommendations(this);
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = () => {
    this.#closeGeneration += 1;
    this.#restoreManagedInert();
  };

  #handleCartLinesUpdate = (event) => {
    const drawer = this.#themeDrawer;
    const closeGenerationAtStart = this.#closeGeneration;
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !drawer?.isOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    let openHandledForThisEvent = false;

    const openIfStillWanted = () => {
      if (!shouldAutoOpen || openHandledForThisEvent) return;
      if (this.#closeGeneration !== closeGenerationAtStart) return;
      if (!drawer?.isOpen) drawer?.open();
      openHandledForThisEvent = true;
    };

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) openIfStillWanted();

    event.promise?.then((result) => {
      const detail = result?.detail;
      const settle = () => requestAnimationFrame(() => {
        this.#updateStickyState();
        mountCartRecommendations(this);
      });
      if (!shouldAutoOpen || detail?.didError) {
        settle();
        return;
      }
      const openAndSettle = () => {
        openIfStillWanted();
        settle();
      };
      if (sourceModal?.open) sourceModal.addEventListener('close', openAndSettle, { once: true });
      else openAndSettle();
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
    });
  };

  #prepareOptionsPanelClose = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const panel = target.closest(`.${PG_CART_OPTIONS_CLASS}`);
    if (!panel) return;
    const isPanelButtonClick = event.type === 'click' && Boolean(target.closest('.pg-cart-options-panel__icon-button'));
    const isEscape = event.type === 'keydown' && event.key === 'Escape';
    if (isPanelButtonClick || isEscape) this.#restoreManagedInert();
  };

  #syncOptionsPanelState = () => {
    const inner = this.querySelector('.cart-drawer__inner');
    if (!(inner instanceof HTMLElement)) return;
    const panel = Array.from(inner.children).find((child) => child.classList?.contains(PG_CART_OPTIONS_CLASS));
    if (!(panel instanceof HTMLElement)) {
      this.#restoreManagedInert();
      return;
    }
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    for (const child of inner.children) {
      if (!(child instanceof HTMLElement) || child === panel || child.inert) continue;
      child.inert = true;
      child.setAttribute(MANAGED_INERT_ATTRIBUTE, 'true');
    }
  };

  #restoreManagedInert() {
    this.querySelectorAll(`[${MANAGED_INERT_ATTRIBUTE}='true']`).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      element.inert = false;
      element.removeAttribute(MANAGED_INERT_ATTRIBUTE);
    });
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    if (drawerHeight <= 0) return;
    dialog.setAttribute('cart-summary-sticky', summaryHeight / drawerHeight > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', StableCartDrawerComponent);
}

import(new URL('./cart-drawer-original-v3.js', import.meta.url).href).catch((error) => {
  console.error('[cart-drawer] Unable to load recommendation module:', error);
});
